
import { Options, Vue } from 'vue-class-component'
import AccountIcon from 'vue-material-design-icons/Account.vue'
import CallIcon from 'vue-material-design-icons/PhoneOutline.vue'
import CameraSwitchIcon from 'vue-material-design-icons/CameraSwitch.vue'
import CloseIcon from 'vue-material-design-icons/Close.vue'
import Confirm from '@/components/Confirm.vue'
import EyeIcon from 'vue-material-design-icons/Eye.vue'
import FaceAgentIcon from 'vue-material-design-icons/FaceAgent.vue'
import MicrophoneIcon from 'vue-material-design-icons/Microphone.vue'
import MicrophoneOffIcon from 'vue-material-design-icons/MicrophoneOff.vue'
import PlayIcon from 'vue-material-design-icons/MonitorShare.vue'
import VideoCallIcon from 'vue-material-design-icons/VideoOutline.vue'
import VideoIcon from 'vue-material-design-icons/Video.vue'
import VideoOffIcon from 'vue-material-design-icons/VideoOff.vue'

const MESSAGE_TYPE = {
  ASK: 'ASK',
  CANDIDATE: 'CANDIDATE',
  PING: 'PING',
  SDP: 'SDP'
}

enum SESSION_KIND {
  CALL = 'CALL',
  SCREENSHARE = 'SCREENSHARE',
  VIDEOCALL = 'VIDEOCALL'
}

interface Viewer {
  id: string
  isCameraEnabled: boolean
  name: string
  pc: RTCPeerConnection
  userMedia?: MediaStream
}

@Options({
  components: {
    CallIcon,
    CameraSwitchIcon,
    CloseIcon,
    Confirm,
    EyeIcon,
    FaceAgentIcon,
    MicrophoneIcon,
    MicrophoneOffIcon,
    AccountIcon,
    PlayIcon,
    VideoCallIcon,
    VideoIcon,
    VideoOffIcon
  }
})
export default class WebRTC extends Vue {
  private askingPermissions = false
  private displayMediaId: string | null = null
  private hasFacingMode = false
  private localMedia: MediaStream | null = null
  private localDisplayMedia: MediaStream | null = null
  private notifications: Array<{ content: string, id: string }> = []
  private pingTimeout = 0
  private showOverlay = false
  private showOverlayTimeout = 0
  private signaling: EventSource | null = null
  private userMediaEnabled: Record<string, boolean | string> = {
    audio: false,
    video: false
  }

  private viewers: Array<Viewer> = []

  get SESSION_KIND (): typeof SESSION_KIND {
    return SESSION_KIND
  }

  get isCameraEnabled (): boolean {
    return this.isUserMediaEnabled('video')
  }

  get isDeviceCompatible (): boolean {
    if (this.isKindScreensharing) {
      return this.hasMediaDeviceAPI
    } else if (this.isKindAudiocall || this.isKindVideocall) {
      return this.hasUserMediaAPI
    } else {
      return this.hasMediaDeviceAPI || this.hasUserMediaAPI
    }
  }

  get isExpired (): boolean {
    const date = this.URLSearchParams.get('expires-at')

    if (date) {
      const expirationDate = new Date(date)
      const nowDate = new Date()
      return expirationDate.getTime() - nowDate.getTime() <= 0
    }

    return false
  }

  get isKindAudiocall (): boolean {
    return this.kind === 'audiocall'
  }

  get isKindScreensharing (): boolean {
    return this.kind === 'screensharing'
  }

  get isKindVideocall (): boolean {
    return this.kind === 'videocall'
  }

  get isMicrophoneEnabled (): boolean {
    return this.isUserMediaEnabled('audio')
  }

  get isReady (): boolean {
    return this.token !== null && !this.isExpired && this.isDeviceCompatible
  }

  get kind (): string | null {
    return this.URLSearchParams.get('kind')
  }

  get token (): string | null {
    return this.URLSearchParams.get('token')
  }

  get URLSearchParams (): URLSearchParams {
    return new URLSearchParams(window.location.search)
  }

  get hasMediaDeviceAPI (): boolean {
    return 'mediaDevices' in navigator && 'getDisplayMedia' in navigator.mediaDevices
  }

  get hasUserMediaAPI (): boolean {
    return 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices
  }

  addViewer (id: string, name: string): void {
    // Generating TURN credential then creating Peer Connection
    fetch(`${process.env.VUE_APP_WEBRTC_URL}/turn`, {
      headers: {
        Authorization: 'Bearer ' + this.token
      },
      method: 'GET'
    })
      .then((response: Response) => {
        if (response.status === 200) {
          return response.json()
        } else if (response.status === 401) {
          window.location.reload()
        }
      })
      .then((turnCredentials: { password: string, username: string }) => {
        this.removeViewerIfExists(id)
        this.viewers.push({
          id,
          isCameraEnabled: false,
          name,
          pc: this.createPeerConnection(
            id,
            turnCredentials.username,
            turnCredentials.password
          )
        })
      })
  }

  allowViewer (id: string, content: string): void {
    this.addViewer(id, content)
    this.removeNotificationIfExists(id)
    this.sendMessage({
      message_type: MESSAGE_TYPE.ASK,
      content: true
    }, id)
  }

  clearLocalMedia (): void {
    this.localMedia = null

    if (this.$refs.localMediaEl) {
      const localMediaEl = this.$refs.localMediaEl as HTMLVideoElement
      localMediaEl.srcObject = this.localMedia
      localMediaEl.pause()
    }
  }

  clearLocalDisplayMedia (): void {
    this.displayMediaId = null
    this.localDisplayMedia = null

    if (this.$refs.localDisplayMediaEl) {
      const localDisplayMediaEl = this.$refs.localDisplayMediaEl as HTMLVideoElement
      localDisplayMediaEl.srcObject = this.localDisplayMedia
      localDisplayMediaEl.pause()
    }
  }

  async createAndSendOffer (id: string, pc: RTCPeerConnection): Promise<void> {
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)

    this.sendMessage({
      message_type: MESSAGE_TYPE.SDP,
      content: offer
    }, id)
  }

  createPeerConnection (id: string, username: string, password: string): RTCPeerConnection {
    const pc = new RTCPeerConnection({
      // iceTransportPolicy: 'relay',
      iceServers: [{
        credential: password,
        urls: process.env.VUE_APP_TURN_URL,
        username
      }]
    })

    pc.onnegotiationneeded = async () => {
      await this.createAndSendOffer(id, pc)
    }

    pc.onicecandidate = (iceEvent) => {
      if (iceEvent && iceEvent.candidate) {
        this.sendMessage({
          message_type: MESSAGE_TYPE.CANDIDATE,
          content: iceEvent.candidate
        }, id)
      }
    }

    pc.ontrack = (event) => {
      const viewer = this.viewers.find(viewer => viewer.pc === event.target)

      if (viewer) {
        if (viewer.userMedia) {
          viewer.userMedia.addTrack(event.track)
        } else {
          const stream = event.streams[0] as MediaStream
          const viewerMediaEl = (this.$refs[viewer.id] as Array<HTMLVideoElement>)[0]
          const localDisplayMediaEl = this.$refs.localDisplayMediaEl as HTMLVideoElement

          stream.onremovetrack = (e) => {
            if (viewer.userMedia) {
              e.track.enabled = false
              e.track.stop()
              viewer.userMedia.removeTrack(e.track)
              viewerMediaEl.srcObject = viewer.userMedia

              if (localDisplayMediaEl.srcObject === viewerMediaEl.srcObject) {
                this.localDisplayMedia = viewer.userMedia
                localDisplayMediaEl.srcObject = this.localDisplayMedia
              }

              if (e.track.kind === 'video') {
                viewer.isCameraEnabled = false
              }

              if (viewer.userMedia.getTracks().length === 0 && viewer.userMedia !== this.localDisplayMedia) {
                viewer.userMedia = undefined
              }
            }
          }

          viewer.userMedia = stream
          viewerMediaEl.srcObject = viewer.userMedia
          if (!localDisplayMediaEl.srcObject) {
            this.localDisplayMedia = viewer.userMedia
            localDisplayMediaEl.srcObject = this.localDisplayMedia
          }
        }

        if (event.track.kind === 'video') {
          viewer.isCameraEnabled = true
        }
      }
    }

    pc.onconnectionstatechange = () => {
      if (['closed', 'disconnected', 'failed'].includes(pc.connectionState)) {
        this.removeViewerIfExists(id)
      }
    }

    this.localMedia?.getTracks()
      .forEach(track => pc.addTrack(
        track,
        this.localMedia as MediaStream
      ))

    if (this.localDisplayMedia) {
      this.localDisplayMedia.getTracks()
        .forEach(track => pc.addTrack(
          track,
          this.localDisplayMedia as MediaStream
        ))
    }

    return pc
  }

  async createSession (kind?: SESSION_KIND): Promise<void> {
    this.askingPermissions = true

    // Getting display media
    try {
      if (kind === SESSION_KIND.VIDEOCALL) {
        await this.toggleAll()
      } else if (kind === SESSION_KIND.CALL) {
        await this.toggleMicrophone()
      } else if (kind === SESSION_KIND.SCREENSHARE) {
        await this.toggleScreensharing()
      }

      // Subcribring to SSE
      if (!this.signaling) {
        this.initSignaling()
      }

      this.askingPermissions = false
    } catch (e) {
      this.askingPermissions = false
      throw e
    }
  }

  mounted (): void {
    if (this.isReady) {
      if (this.isKindAudiocall) {
        this.createSession(SESSION_KIND.CALL)
      } if (this.isKindVideocall) {
        this.createSession(SESSION_KIND.VIDEOCALL)
      } else if (this.isKindScreensharing) {
        this.createSession(SESSION_KIND.SCREENSHARE)
      }
    }
  }

  denyViewer (id: string): void {
    this.sendMessage({
      message_type: MESSAGE_TYPE.ASK,
      content: false
    }, id)
    this.removeNotificationIfExists(id)
  }

  destroySession (): void {
    if (this.localMedia) {
      this.toggleCamera(false)
      this.toggleMicrophone(false)
      this.clearLocalMedia()
      this.clearLocalDisplayMedia()
    }

    if (this.localDisplayMedia) {
      this.toggleScreensharing(false)
      this.clearLocalDisplayMedia()
    }

    if (this.viewers.length) {
      this.viewers.forEach(viewer => viewer.pc.close())
      this.viewers = []
    }

    if (this.signaling) {
      this.signaling.close()
      this.signaling.removeEventListener('json', this.signalHandler)
      this.signaling.removeEventListener('closed', this.destroySession)
      this.signaling = null
    }

    if (this.kind !== null) {
      this.$nextTick(() => {
        window.close()
      })
    }
  }

  getPeerConnection (id: string): RTCPeerConnection | undefined {
    return this.viewers.find(viewer => viewer.id === id)?.pc
  }

  initSignaling (): void {
    this.signaling = new EventSource(`${process.env.VUE_APP_WEBRTC_URL}/sse?kind=webrtc&feature=${this.kind}&token=${this.token}`)
    this.signaling.addEventListener('json', this.signalHandler)
    this.signaling.addEventListener('closed', this.destroySession)
  }

  isUserMediaEnabled (kind: string): boolean {
    return Boolean(this.userMediaEnabled[kind])
  }

  pingScreen ({ x, y }: { x: number, y: number}): void {
    const bbox = (this.$refs.localDisplayMediaEl as HTMLVideoElement).getBoundingClientRect()
    const ping = this.$refs.ping as HTMLElement

    if (!ping) {
      return
    }

    clearTimeout(this.pingTimeout)

    ping.style.left = x * bbox.width + bbox.left + 'px'
    ping.style.top = y * bbox.height + bbox.top + 'px'

    this.pingTimeout = setTimeout(() => {
      this.pingTimeout = 0
    }, 3000)
  }

  removeNotificationIfExists (id: string): void {
    const index = this.notifications.findIndex(n => n.id === id)
    if (index >= 0) {
      this.notifications.splice(index, 1)
    }
  }

  removeViewerIfExists (id: string): void {
    const index = this.viewers.findIndex(viewer => viewer.id === id)
    const localDisplayMediaEl = this.$refs.localDisplayMediaEl as HTMLVideoElement

    if (index >= 0) {
      const viewer = this.viewers[index]

      if (viewer.userMedia === this.localDisplayMedia) {
        this.localDisplayMedia = null
        localDisplayMediaEl.srcObject = this.localDisplayMedia
        localDisplayMediaEl.pause()
      }

      viewer.pc.close()
      this.viewers.splice(index, 1)
    }
  }

  sendMessage (message: { content: unknown, 'message_type': string }, to: string): void {
    fetch(`${process.env.VUE_APP_WEBRTC_URL}/sessions/${to}`, {
      body: JSON.stringify(message),
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + this.token,
        'Content-Type': 'application/json'
      },
      method: 'POST'
    })
  }

  async signalHandler (message: Event): Promise<void> {
    const data = JSON.parse((message as any).data)

    if (!data) {
      return
    }

    const from = data.from
    const { content, message_type: messageType } = data.payload
    const pc = this.getPeerConnection(from)

    if (messageType === MESSAGE_TYPE.CANDIDATE && content && pc) {
      await pc.addIceCandidate(content)
    } else if (messageType === MESSAGE_TYPE.SDP && pc) {
      if (content.type === 'offer') {
        await pc.setRemoteDescription(content)
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        this.sendMessage({
          message_type: MESSAGE_TYPE.SDP,
          content: answer
        }, from)
      } else if (content.type === 'answer') {
        await pc.setRemoteDescription(content)
      }
    } else if (messageType === MESSAGE_TYPE.ASK) {
      this.removeNotificationIfExists(from)
      this.notifications.push({
        content,
        id: from
      })
    } else if (messageType === MESSAGE_TYPE.PING) {
      this.pingScreen(content)
    }
  }

  async switchCamera (): Promise<void> {
    if (!this.isCameraEnabled) {
      return
    }

    const facingMode = this.userMediaEnabled.video === 'user'
      ? 'environment'
      : 'user'

    await this.toggleCamera(false)
    await this.toggleCamera(true, facingMode)
  }

  async toggleAll (): Promise<void> {
    await this.toggleUserMedia(null)
  }

  async toggleCamera (force?: boolean, facingMode?: 'user' | 'environment'): Promise<void> {
    await this.toggleUserMedia('video', force, facingMode)
  }

  async toggleMicrophone (force?: boolean): Promise<void> {
    await this.toggleUserMedia('audio', force)
  }

  async toggleUserMedia (kind: 'audio' | 'video' | null, force?: boolean, facingMode = 'user'): Promise<void> {
    const localMediaEl = this.$refs.localMediaEl as HTMLVideoElement
    const isUserMediaEnabled = kind ? this.isUserMediaEnabled(kind) : false

    if (force === false || isUserMediaEnabled) {
      this.viewers.forEach(viewer => {
        viewer.pc
          .getSenders()
          .forEach(sender => {
            if (sender.track?.kind === kind && sender.track?.id !== this.displayMediaId) {
              sender.track.enabled = false
              sender.track.stop()
              viewer.pc.removeTrack(sender)
            }
          })
      })

      this.localMedia?.getTracks().forEach(track => {
        if (track.kind === kind) {
          track.enabled = false
          track.stop()
          this.localMedia?.removeTrack(track)
        }
      })

      if (this.localMedia?.getTracks().length === 0) {
        this.clearLocalMedia()
      } else {
        localMediaEl.srcObject = this.localMedia
      }
    } else {
      let userMediaDesc

      switch (kind) {
        case 'audio':
          userMediaDesc = {
            audio: {
              echoCancellation: true,
              noiseSuppression: true
            }
          }
          break
        case 'video':
          userMediaDesc = {
            video: {
              facingMode
            }
          }
          break
        default:
          userMediaDesc = {
            audio: {
              echoCancellation: true,
              noiseSuppression: true
            },
            video: {
              facingMode
            }
          }
          break
      }

      const stream = await navigator.mediaDevices.getUserMedia(userMediaDesc)

      if (!this.localMedia) {
        this.localMedia = stream
        localMediaEl.srcObject = this.localMedia
      }

      stream
        .getTracks()
        .forEach(track => {
          if (this.localMedia !== stream) {
            this.localMedia?.addTrack(track)
          }

          if (!this.hasFacingMode && track.getCapabilities().facingMode?.length) {
            this.hasFacingMode = true
          }

          this.viewers.forEach(viewer => {
            viewer.pc.addTrack(track, this.localMedia as MediaStream)
          })
        })
    }

    const requestedKinds = kind
      ? [kind]
      : Object.keys(this.userMediaEnabled)

    requestedKinds.forEach(kind => {
      if (kind === 'video') {
        this.userMediaEnabled[kind] = !isUserMediaEnabled
          ? facingMode
          : false
      } else {
        this.userMediaEnabled[kind] = !isUserMediaEnabled
      }
    })
  }

  toggleOverlay (force?: boolean): void {
    this.showOverlay = force !== undefined
      ? force
      : !this.showOverlay

    if (this.showOverlay) {
      if (this.showOverlayTimeout) {
        clearTimeout(this.showOverlayTimeout)
      }

      this.showOverlayTimeout = setTimeout(() => {
        this.showOverlay = false
      }, 5000)
    }
  }

  async toggleScreensharing (force?: boolean): Promise<void> {
    const localDisplayMediaEl = this.$refs.localDisplayMediaEl as HTMLVideoElement
    const isEnabled = Boolean(this.displayMediaId)

    if (force === false || isEnabled) {
      this.viewers.forEach(viewer => {
        viewer.pc
          .getSenders()
          .forEach(sender => {
            if (sender.track?.id === this.displayMediaId) {
              sender.track.enabled = false
              sender.track.stop()
              viewer.pc.removeTrack(sender)
            }
          })
      })

      this.localDisplayMedia?.getTracks().forEach(track => {
        track.enabled = false
        track.stop()
        this.localDisplayMedia?.removeTrack(track)
      })

      this.clearLocalDisplayMedia()
    } else {
      const stream = await navigator.mediaDevices.getDisplayMedia()

      this.localDisplayMedia = stream
      localDisplayMediaEl.srcObject = this.localDisplayMedia

      stream
        .getTracks()
        .forEach(track => {
          this.viewers.forEach(viewer => {
            viewer.pc.addTrack(track)
          })
          this.displayMediaId = track.id
        })
    }
  }
}
